import type { PartialDeep } from 'type-fest'

export const useBusinessStore = defineStore('business', {
  state: (): BusinessData => ({
    id: '',
    admins: [],
    members: [],
    invitedMembers: [],
    domains: [],
    email: '',
    name: '',
    phone: '',
    createdAt: '',
    gtcAcceptedAt: '',
    dpaAcceptedAt: '',
    billing: {
      address1: '',
      address2: '',
      city: '',
      country: '',
      postalCode: '',
      vat: '',
      currency: undefined,
      paymentMethod: undefined,
      paymentStatus: undefined,
      stripeSubscriptionStatus: undefined,
    },
    pricePlan: {
      licenses: {
        idents: {
          eidas: {
            payForMembers: false,
            payForInvitations: false,
            identMethod: undefined,
          },
          zertes: {
            payForMembers: false,
            payForInvitations: false,
            identMethod: undefined,
          },
        },
      },
      current: {
        base: null,
      },
      next: {
        base: null,
      },
      discounts: [],
    },
    settings: {
      supportContact: { email: '', phone: '' },
      signatureRequests: { part11: { enabled: false } },
      companyBranding: {
        logo: { height: '', width: '', url: '' },
        miniLogo: { height: '', width: '', url: '' },
      },
      mailService: {
        attachments: {
          signedDocument: { enabled: false, customisable: false },
          signatureProtocol: { enabled: false, customisable: false },
        },
      },
      groups: {
        maxGroups: 0,
        maxParticipants: 0,
        maxObserverGroupsPerSr: 0,
      },
      identShop: { url: '' },
    },
    seals: [],
    stripe: {
      subscriptionStatus: '',
    },
    aes4biz: {
      enabled: false,
      representativeName: '',
      representativeEmail: '',
      commercialRegister: '',
      domains: [],
    },
    dashboard: null,
    emailDomain: null,
    defaultSender: '',
  }),
  getters: {
    branding: state => {
      if ('companyBranding' in state.settings) {
        if (state.settings.companyBranding?.logo !== undefined) {
          return state.settings.companyBranding
        } else {
          return Object.assign({}, state.settings.companyBranding, {
            logo: { url: '' },
            miniLogo: { height: '', width: '', url: '' },
          })
        }
      }
      return {
        logo: {
          url: '',
        },
        miniLogo: {
          url: '',
        },
      }
    },
    currentPricePlan(state): ActivePricePlan | null {
      return state.pricePlan.current.base
    },
    isGtcAccepted(state): boolean {
      return Boolean(state.gtcAcceptedAt)
    },
    previousPricePlans(state): { base: ActivePricePlan }[] {
      return state.pricePlan.previous || []
    },
    /**
     * Infers the plan name from the plan id.
     *
     * We don't get a human-readable name from the backend, so we need to compute it.
     */
    currentPricePlanName(): PricePlanName | '' {
      const id = this.currentPricePlan?.type
      const inferredName = id?.split('_').shift()
      if (!inferredName) return ''
      return (inferredName.charAt(0) + inferredName.slice(1).toLowerCase()) as PricePlanName
    },
    currentPaymentStatus(): PaymentStatus | null {
      return this.billing.paymentStatus || null
    },
    isStarterPlan(): boolean {
      return Boolean(this.currentPricePlan?.type.startsWith('STARTER'))
    },
    hasPreviouslyUsedTrial(): boolean {
      return this.previousPricePlans.some(plan => plan.base.trial || plan.base.type.startsWith('TRIAL_V1'))
    },
    isCancelling(): boolean {
      return this.pricePlan?.next?.base?.type === 'CANCELED'
    },
    isCancelled(): boolean {
      return this.currentPricePlan?.type === 'CANCELED'
    },
    isTrialing(state): boolean {
      return state.billing.paymentMethod === 'TRIALING' || Boolean(state.pricePlan.current.base?.trial)
    },
    isChargebeeTrial(state): boolean {
      return Boolean(state.pricePlan.current.base?.trial)
    },
    isStarterOrCbTrial(): boolean {
      /**
       * For the Starter and CB Trial, we update an ongoing subscription in CB, this forces us
       * to keep the same currency.
       */
      return this.isChargebeeTrial || this.isStarterPlan
    },
    isTrialExpired(state): boolean {
      return state.billing.paymentMethod === 'NEEDS_PAYMENT_METHOD'
    },
    isLocked(state): boolean {
      return state.billing.paymentMethod === 'BLOCKED'
    },
    isPaymentUpdateRequired(state): boolean {
      return state.billing.paymentMethod === 'BLOCKED' && state.billing.paymentStatus === 'UPDATE_REQUIRED'
    },
    isManuallyInvoiced(state): boolean {
      return state.billing.paymentMethod === 'MANUAL'
    },
    isChargebeeBusiness(state): boolean {
      return state.billing.paymentMethod === 'CHARGEBEE'
    },
    trialEndDate(state): Date | null {
      if (!this.isTrialing) return null
      const trialEndDateString = state.pricePlan.current.base?.endAt
      return trialEndDateString ? new Date(trialEndDateString) : null
    },
    hasPaymentIssues(state): boolean {
      return (
        (!this.isLocked &&
          state.stripe.subscriptionStatus &&
          !['active', 'incomplete_expired'].includes(state.stripe.subscriptionStatus)) ||
        Boolean(
          state.stripe?.subscriptionStatus &&
          !['active', 'incomplete_expired'].includes(state.stripe?.subscriptionStatus)
        )
      )
    },
    companyHasAutoDeletionEnabled(state) {
      return state.settings?.signatureRequests?.retention?.enabled ?? false
    },
    companyHasLegacyAutoDeletion(state) {
      return state.settings?.signatureRequests?.retention?.terminalState ?? false
    },
    companyHasPart11(state) {
      return state.settings?.signatureRequests?.part11?.enabled ?? false
    },
    hasDiscountApplied(state) {
      return Boolean(state.pricePlan.discounts?.length)
    },
    discountPromoCode(state) {
      if (!this.hasDiscountApplied) return null
      return state.pricePlan.discounts?.[0].promoCode
    },
    discountType(state) {
      if (!this.hasDiscountApplied) return null
      return state.pricePlan.discounts?.[0].type || ''
    },
    discountExpirationDate(state) {
      if (!this.hasDiscountApplied) return null
      const expiresAt = state.pricePlan.discounts?.[0].expiresAt
      if (!expiresAt) return null
      return new Date(expiresAt)
    },
    isDiscountActive(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0].status === 'ACTIVE'
    },
    isDiscountUsedUp(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0].status === 'USED_UP'
    },
    isSMGDiscountApplied(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0]?.type?.toLocaleLowerCase().startsWith('smg_')
    },
    isIdentificationActive(state) {
      return (
        (state.pricePlan.licenses?.idents?.eidas.payForInvitations ||
          state.pricePlan.licenses?.idents?.eidas.payForMembers ||
          state.pricePlan.licenses?.idents?.zertes.payForInvitations ||
          state.pricePlan.licenses?.idents?.zertes.payForMembers) ??
        false
      )
    },
    isZertesActiveForMembers(state) {
      return state.pricePlan.licenses?.idents?.zertes.payForMembers ?? false
    },
    isEidasActiveForMembers(state) {
      return state.pricePlan.licenses?.idents?.eidas.payForMembers ?? false
    },
    isIdentMethodNect(state) {
      return state.pricePlan.licenses?.idents?.eidas.identMethod === 'nect'
    },
    isCelloEnabled(state) {
      return Boolean(state.settings.affiliate?.enabled)
    },
    isScimEnabled(state) {
      return Boolean(state.settings.scim?.enabled)
    },
    isDirectSignEnabled(state) {
      return state.settings?.mabSettings?.directSign?.enabled ?? false
    },
    groupsLimit(state) {
      return state.settings.groups
    },
    observerGroupsLimit(state) {
      return state.settings.groups.maxObserverGroupsPerSr
    },
    membersCount(state): number {
      return state.members.length
    },
  },
  actions: {
    async registerBusiness(registerData: RegisterBusinessPayload) {
      const { businessRepository } = useApi()
      const { track } = useAnalytics()

      const businessData = await businessRepository.create(registerData)

      if (registerData.attributes?.companySize || registerData.attributes?.skribbleSigningVolume) {
        track(ANALYTICS_EVENTS.BUSINESS_REGISTERED, {
          businessId: businessData.id,
          companySize: registerData.attributes?.companySize,
          signingVolume: registerData.attributes?.skribbleSigningVolume,
        })
      }

      this.$patch(businessData)
    },
    async getBusiness() {
      if (!this.id) return

      const { businessRepository } = useApi()
      const businessData = await businessRepository.get(this.id)
      // test mocks sometimes send null response which turns to '', so this guard is needed to prevent undefined erros
      if (!businessData) return

      // override groups property because v1 is returning 0 values always
      businessData.settings = {
        ...businessData.settings,
        groups: this.groupsLimit,
      }

      this.$patch(businessData)
    },
    async acceptGtc() {
      const { businessRepository } = useApi()
      const updatedData = await businessRepository.acceptGtc(this.id)
      this.$patch(updatedData)
    },
    async getBillingAddress() {
      const { businessRepository } = useApi()
      const billing = await businessRepository.getBillingAddress(this.id)

      this.$patch({ billing })
    },
    async convertFreeToStarter(gtc: boolean, currency: string) {
      const { businessRepository } = useApi()
      const businessData = await businessRepository.convertFreeToStarter({ gtc, currency })

      this.$patch(businessData)
    },
    async updateContactInfo(contactInfo: Partial<BusinessContactInfo>) {
      const { businessRepository } = useApi()
      const updatedData = await businessRepository.updateContactInfo(this.id, contactInfo)
      this.$patch(updatedData)
    },
    async updateSupportContact(contactData: SupportContactData) {
      const { businessRepository } = useApi()

      const updatedData = await businessRepository.updateSupportContact(this.id, {
        settings: { supportContact: contactData },
        // FIXME: Backend expects all below as well, which doesn't make a lot of sense
        name: this.name,
        email: this.email,
        phone: this.phone,
      })

      this.$patch(updatedData)
      this.parseAndPatchSettings(updatedData.settings)
    },
    async updateBillingAddress(addressData: Partial<Address>) {
      const { businessRepository } = useApi()
      const updatedData = await businessRepository.updateBillingAddress(this.id, addressData)
      this.$patch(updatedData)
    },
    async deleteBusiness() {
      const { businessRepository } = useApi()
      await businessRepository.delete(this.id)
    },
    async revokeCancelBusiness() {
      const { businessRepository } = useApi()
      await businessRepository.revokeDeletion(this.id)
      await this.getBusiness()

      if (this.pricePlan?.next?.base?.type === 'CANCELED') {
        this.pricePlan.next.base = null
      }
    },
    async getApiUsers(): Promise<APIKeyList> {
      const { businessRepository } = useApi()
      return businessRepository.getApiUsers(this.id)
    },
    async createApiUser(data: {
      type: APIKeyType
      description: string
      role?: APIKeyRole
    }): Promise<{ user: APIKeyCreateUser; status?: string }> {
      const { businessRepository } = useApi()
      return await businessRepository.createApiUser(this.id, data)
    },
    async deleteApiUser(username: string) {
      const { businessRepository } = useApi()
      return await businessRepository.deleteApiUser(this.id, username)
    },
    async resetApiUser(username: string): Promise<{ user: APIKeyReset }> {
      const { businessRepository } = useApi()
      return await businessRepository.resetApiUser(this.id, username)
    },
    async updateApiUser(data: { username: string; description: string }): Promise<{ user: APIKeyUpdate }> {
      const { businessRepository } = useApi()
      return await businessRepository.updateApiUser(this.id, data)
    },
    async getSeals() {
      if (!this.id) return

      const { businessRepository } = useApi()
      const seals = await businessRepository.getSeals(this.id)

      this.$patch({ seals })
    },
    async getMembers() {
      const { businessRepository } = useApi()
      const members = await businessRepository.getMembers(this.id)
      this.$patch({ members: members.map(member => member.username) })
      return members
    },
    async addMembers(userEmails: string[]) {
      const { businessRepository } = useApi()
      return businessRepository.addMembers(this.id, userEmails)
    },
    async removeMember(userIdOrIds: string | string[]) {
      const { businessRepository } = useApi()
      if (Array.isArray(userIdOrIds)) {
        await businessRepository.removeMembers(this.id, userIdOrIds)
      } else {
        await businessRepository.removeMember(this.id, userIdOrIds)
      }
    },
    async makeAdmin(userIdOrIds: string | string[]) {
      const { businessRepository } = useApi()
      if (Array.isArray(userIdOrIds)) {
        await businessRepository.makeAdmins(this.id, userIdOrIds)
      } else {
        await businessRepository.makeAdmin(this.id, userIdOrIds)
      }
    },
    async revokeAdmin(userIdOrIds: string | string[]) {
      const { businessRepository } = useApi()
      if (Array.isArray(userIdOrIds)) {
        await businessRepository.revokeAdmins(this.id, userIdOrIds)
      } else {
        await businessRepository.revokeAdmin(this.id, userIdOrIds)
      }
    },
    async revokeInvite(userId: string) {
      const { businessRepository } = useApi()
      await businessRepository.revokeInvite(this.id, userId)
    },
    async addDomain(domain: string) {
      const { businessRepository } = useApi()
      const domains = await businessRepository.addDomain(this.id, domain)
      this.$patch({ domains })
    },
    async removeDomain(domain: string) {
      const { businessRepository } = useApi()
      const domains = await businessRepository.removeDomain(this.id, domain)
      this.$patch({ domains })
    },
    async getSettings() {
      if (!this.id) return
      const { businessRepository } = useApi()
      this.parseAndPatchSettings(await businessRepository.getSettings(this.id))
    },
    async updateSettings(settings: PartialDeep<BusinessPreferences>) {
      if (!this.id) return
      const { businessRepository } = useApi()
      this.parseAndPatchSettings(await businessRepository.updateSettings(this.id, settings))
    },
    async cancelTrial() {
      const { businessRepository } = useApi()
      return businessRepository.cancelTrial(this.id)
    },
    parseAndPatchSettings(settings: BusinessPreferences) {
      if (settings.supportContact) {
        /* remove possibly bad email, as this comes from user defined value */
        if (settings.supportContact.email && !isEmail(settings.supportContact.email)) {
          settings.supportContact.email = ''
        }
        /* remove possibly bad phone, as this comes from user defined value */
        if (settings.supportContact.phone && !isPhoneNumber(settings.supportContact.phone)) {
          settings.supportContact.phone = ''
        }
      }
      this.$patch({ settings })
    },
    async getEmailDomain() {
      if (!this.id) return
      const { businessRepository } = useApi()
      const emailDomain = await businessRepository.getEmailDomain(this.id)
      this.$patch({ emailDomain })
    },
    async getDefaultSender() {
      if (!this.id) return
      const { businessRepository } = useApi()
      const defaultSender = await businessRepository.getDefaultSender(this.id)
      this.$patch({ defaultSender })
    },
    async registerEmailDomain(payload: RegisterEmailDomainPayload) {
      const { businessRepository } = useApi()
      const emailDomain = await businessRepository.registerEmailDomain(this.id, payload)
      this.$patch({ emailDomain })
    },
    async updateEmailDomain(updates: UpdateEmailDomainPayload) {
      const { businessRepository } = useApi()
      const emailDomain = await businessRepository.updateEmailDomain(this.id, updates)
      this.$patch({ emailDomain })
    },
    async verifyEmailDomain() {
      const { businessRepository } = useApi()
      const emailDomain = await businessRepository.verifyEmailDomain(this.id)
      this.$patch({ emailDomain })
    },
    async removeEmailDomain() {
      const { businessRepository } = useApi()
      await businessRepository.removeEmailDomain(this.id)
      this.$patch({ emailDomain: null })
    },
    async loadDashboard() {
      const { businessRepository } = useApi()
      const dashboard = await businessRepository.getDashboard(this.id)
      this.$patch({ dashboard })
    },
    clearBusiness() {
      this.$reset()
    },
    async getSubscriptionPrices(): Promise<PricePlanPrices> {
      const { businessRepository } = useApi()
      return businessRepository.getSubscriptionPrices(this.id)
    },
    async updateChargebeeLanguage(language: Language) {
      const { businessRepository } = useApi()
      return businessRepository.updateChargebeeLanguage(this.id, language)
    },
  },
})
